import { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { format, isToday } from 'date-fns'

import { getNameInitials } from '@/helpers/getNameInitials'
import { Log, MandatoryLogInformation, RouteSelectionMode } from '@/types/logs'
import { CommonHeader, filterByTimeRange, tableCellStyles, TimeHeader } from '@/ui/Table'

import { AuthorTooltip } from '../../ui/components/TableForm/tooltips/AuthorTooltip'
import { Code } from '../../ui/components/information/Code'
import { COMBINED_LOGS_COLUMNS } from '../../constant'
import { Reference } from '../../ui/components/information/Reference'
import { Routes } from '../../ui/components/information/Routes'
import { Trip } from '../../ui/components/information/Trip'
import { TripNestedInfo } from '../../ui/components/information/TripNestedInfo'
import { Location } from '../../ui/components/information/Location'
import { Description } from '../../ui/components/information/Description'
import { Time } from '@/components/Time'
import { sortLogsByUnfinished } from '../../helpers/sortLogsByUnfinished'
import { filterLogsBy } from '../../helpers/filterLogsBy'
import { LogAction } from '../../ui/components/information/LogAction'
import { Unfinished } from '../../ui/components/information/Unfinished'
import { FlaggedCell } from '../../ui/components/information/FlaggedCell'
import { useCheckPermissions } from '@/permissions/useCheckPermissions'
import ChevronRightIcon from '../../assets/chevron-right.svg'
import { useCombinedLogsTable } from '../useCombinedLogsTable'
import { sortLogsBy } from '../../helpers/sortLogsBy'
import { useCachedGetCodeName } from '../useCachedGetCodeName'
import { useManageLogOriginalRoute } from '../useManageLogOriginalRoute'
import { useBusStops } from '@/pages/UnfinishedRecords/hooks/useBusStops'
import { useDateStore } from '@/components/DateInput'
import { CommentIcon } from '../../ui/components/information/CommentIcon'
import FlagIcon from '../../assets/flag.svg'

export const useUnfinishedLogsColumns = (isDataLoading: boolean, actionCallback?: (log: Log) => void) => {
    const columnHelper = createColumnHelper<Log>()
    const hasPermission = useCheckPermissions()
    const { setRowSelection } = useCombinedLogsTable()
    const getCodeName = useCachedGetCodeName()
    const { getElementAttributes } = useManageLogOriginalRoute(isDataLoading)
    const { getRouteBusStops, allStops: allStopsData } = useBusStops()

    const handleArrowClick = (log: Log) => {
        setRowSelection({ [log.id]: true })
        actionCallback && actionCallback(log)
    }

    const { date } = useDateStore()
    const isTodayVal = isToday(new Date(date))

    return useMemo(
        () => [
            columnHelper.accessor('code', {
                id: COMBINED_LOGS_COLUMNS.Code,
                header: (context) => (
                    <CommonHeader
                        label="Code"
                        buttonLabel="Sort by code"
                        searchPlaceholder="Type to search for code"
                        {...context}
                    />
                ),
                cell: (info) => <Code {...info} getCodeName={getCodeName} />,
                filterFn: filterLogsBy((log) => !log.code),
                meta: {
                    isPlaceholder: true,
                    placeholder: 'Code',
                    alignCenter: true,
                    width: '7.143rem',
                },
            }),
            columnHelper.accessor('reference', {
                id: COMBINED_LOGS_COLUMNS.Reference,
                header: 'Reference',
                cell: (info) => <Reference {...info} isDataLoading={isDataLoading} />,
                meta: {
                    alignCenter: true,
                    isPlaceholder: true,
                    placeholder: '-',
                    width: '7.143rem',
                },
            }),
            columnHelper.accessor('routes', {
                id: COMBINED_LOGS_COLUMNS.Routes,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by routes"
                        searchPlaceholder="Type to search for routes"
                        label="Route"
                    />
                ),
                cell: (info) => <Routes {...info} isDataLoading={isDataLoading} />,
                filterFn: filterLogsBy((log) => log.routeSelectionMode === RouteSelectionMode.None),
                meta: {
                    alignCenter: true,
                    width: '5rem',
                    isPlaceholder: true,
                    placeholder: 'Route',
                },
            }),
            columnHelper.accessor('trip', {
                id: COMBINED_LOGS_COLUMNS.Trip,
                header: 'Trip',
                cell: (info) => (
                    <Trip {...info} isDataLoading={isDataLoading} getElementAttributes={getElementAttributes} />
                ),
                meta: {
                    alignCenter: true,
                    width: '5.714rem',
                },
            }),
            columnHelper.accessor('run', {
                id: COMBINED_LOGS_COLUMNS.Run,
                header: 'Run',
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Run}
                        label="run"
                        isDataLoading={isDataLoading}
                        {...info}
                    />
                ),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Run, (log) => !log.run),
                filterFn: filterLogsBy((log) => !!log.run),
                meta: {
                    alignCenter: true,
                    width: '4.286rem',
                },
            }),
            columnHelper.accessor('duty', {
                id: COMBINED_LOGS_COLUMNS.Duty,
                header: 'Duty',
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Duty}
                        label="duty"
                        isDataLoading={isDataLoading}
                        formatValue={(value) => (value as string[]).join('/')}
                        {...info}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.duty.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Duty, (log) => !log.duty?.length),
                meta: {
                    alignCenter: true,
                    width: '4.286rem',
                },
            }),
            columnHelper.accessor('fleet', {
                id: COMBINED_LOGS_COLUMNS.Fleet,
                header: 'Fleet',
                cell: (info) => (
                    <TripNestedInfo
                        field={MandatoryLogInformation.Fleet}
                        label="fleet"
                        isDataLoading={isDataLoading}
                        formatValue={(value) => (value as string[]).join('/')}
                        {...info}
                    />
                ),
                filterFn: filterLogsBy((log) => !log.fleet?.length),
                sortingFn: sortLogsBy(COMBINED_LOGS_COLUMNS.Fleet, (log) => !log.fleet?.length),
                meta: {
                    alignCenter: true,
                    width: '5rem',
                },
            }),
            columnHelper.accessor(
                (row) => ({
                    locationFrom: row.locationFrom,
                    locationTo: row.locationTo,
                    locationType: row.locationType,
                    direction: row.direction,
                }),
                {
                    id: COMBINED_LOGS_COLUMNS.Location,
                    header: 'Location',
                    cell: (info) => (
                        <Location {...info} getRouteBusStops={getRouteBusStops} allStopsData={allStopsData} />
                    ),
                    meta: {
                        width: '18.643rem',
                    },
                }
            ),
            columnHelper.accessor('descriptionInfo', {
                id: COMBINED_LOGS_COLUMNS.Description,
                header: 'Description',
                cell: (info) => <Description {...info} />,
                meta: {
                    width: '18.643rem',
                },
            }),
            columnHelper.accessor('delayInMinutes', {
                id: COMBINED_LOGS_COLUMNS.Delay,
                header: 'Delay',
                cell: (info) => {
                    const value = info.getValue()

                    if (!value) {
                        return '-'
                    }

                    return <span className={tableCellStyles.greyCell}>{`${value} ${!isNaN(value) ? 'min' : ''}`}</span>
                },
                meta: {
                    alignCenter: true,
                    width: '3.571rem',
                },
            }),
            columnHelper.accessor('mileage', {
                id: COMBINED_LOGS_COLUMNS.Milage,
                header: 'Mileage',
                cell: (info) => (
                    <span className={tableCellStyles.greyCell}>{info.getValue() ? info.getValue() : '-'}</span>
                ),
                meta: {
                    alignCenter: true,
                    width: '4.286rem',
                    isPlaceholder: true,
                    placeholder: '-',
                },
            }),
            columnHelper.accessor('originalAuthor', {
                id: COMBINED_LOGS_COLUMNS.OriginalAuthor,
                header: 'Author',
                cell: (info) => (
                    <AuthorTooltip author={info.getValue() || '-'}>
                        <span className={tableCellStyles.greyCell}>
                            {info.getValue() ? getNameInitials(info.getValue()) : '-'}
                        </span>
                    </AuthorTooltip>
                ),
                meta: {
                    alignCenter: true,
                },
            }),
            columnHelper.accessor('timestamp', {
                id: COMBINED_LOGS_COLUMNS.Time,
                header: (context) => (
                    <TimeHeader
                        {...context}
                        label="Time"
                        buttonLabel="Sort by time"
                        includePreviousDay={false}
                        limitToTime={isTodayVal ? new Date() : undefined}
                    />
                ),
                cell: (info) => <span className={tableCellStyles.greyCell}>{format(info.getValue(), 'HH:mm')}</span>,
                filterFn: filterByTimeRange,
                meta: {
                    alignCenter: true,
                    width: '4.286rem',
                    isPlaceholder: true,
                    placeholder: <Time />,
                },
            }),
            columnHelper.accessor((row) => row.status, {
                header: '',
                id: COMBINED_LOGS_COLUMNS.Unfinished,
                cell: ({ row }) => {
                    if (hasPermission('log', 'manageUnfinished')) {
                        return <Unfinished log={row.original} />
                    }

                    return <LogAction log={row.original} />
                },
                enableSorting: true,
                sortingFn: sortLogsByUnfinished,
                meta: {
                    isPlaceholder: true,
                    width: '10.285rem',
                    alignCenter: true,
                },
            }),
            columnHelper.display({
                id: COMBINED_LOGS_COLUMNS.Flag,
                header: () => (
                    <span className={tableCellStyles.iconHeader}>
                        <FlagIcon />
                    </span>
                ),
                cell: ({ row }) => <FlaggedCell log={row.original} />,
                meta: {
                    alignCenter: true,
                    width: '5rem',
                },
            }),
            columnHelper.display({
                id: 'comments',
                header: () => null,
                cell: ({ row }) => <CommentIcon log={row.original} />,
            }),
            columnHelper.display({
                id: 'arrowLeft',
                cell: ({ row }) => (
                    <button data-action type="button" onClick={() => handleArrowClick(row.original)}>
                        <ChevronRightIcon />
                    </button>
                ),
                meta: {
                    alignCenter: true,
                    width: '2.5rem',
                },
            }),
        ],
        [isDataLoading, allStopsData]
    )
}
